/**
 * @file components/biz/HooksList
 * @author zhangzhe(zhangzhe@baidu.com)
 */

import React, {useMemo} from 'react';
import classNames from 'classnames';
import {Table, Pagination} from 'acud';
import {useOriginalCopy} from '@huse/previous-value';
import {ButtonType} from 'acud/lib/button/button';

import {DU_PREFIX} from '../../_common/constants';
import BulkArea from './BulkArea';
import FilterArea from './FilterArea';
import Cards from './Cards';

import './index.less';

const CLS_PREFIX = `${DU_PREFIX}-listpage`;

export type ACTION_TYPE = 'link' | 'ajax' | 'dialog' | 'drawer' | 'custom';

export interface DialogOptionBody {
    type: 'alert' | 'plain' | 'common';
    content: any; // 内容，可以是string、可以是组件
    $payloadFields?: any;
    $extraPayload?: any;
    $onBeforeDialogOpen?: (payload: any) => any;
}

export interface DialogOption {
    body: DialogOptionBody;
    className?: string;
    title?: string;
    width?: number;
    maskClosable?: boolean;
    foot?: boolean;
    okText?: string;
    cancelText?: string;
    cancelWithSuccess?: boolean;
    fromTable?: boolean;
    mask?: boolean;
}

export interface DrawerOptionBody {
    content: any; // 内容，可以是string、可以是组件
    $payloadFields?: any;
    $extraPayload?: any;
    $onBeforeOpen?: (payload: any) => any;
}

export interface DrawerOption {
    body: DrawerOptionBody;
    className?: string;
    title?: string;
    width?: number;
    maskClosable?: boolean;
    foot?: boolean;
    okText?: string;
    cancelText?: string;
    cancelWithSuccess?: boolean;
    fromTable?: boolean;
    mask?: boolean;
}

export type ConfirmType = 'warning' | 'info' | 'error' | 'sucess';

export interface AjaxOption {
    apiAction: string; // redux的action方法名
    $toastMessage?: string;
    cancelText?: string;
    okText?: string;
    confirmTitle?: string; // 确认提示title
    confirmText?: string; // 确认提示文案
    confirmType?: ConfirmType; // 确认按钮的类别
    $onRequest?: (requestPayload: any) => any; // 发起ajax请求前的勾子函数
    $onResponse?: (response: any, requestPayload: any) => any;
    $onError?: (error: any, requestPayload: any) => void;
    $payloadFields?: string[];
    $extraPayload?: any; // 额外的选项
}

export interface BulkItem {
    position?: string; // 不传代表left
    id: string;
    text: string;
    icon?: React.ReactNode;
    type?: 'button' | 'checkbox';
    skin?: ButtonType | 'danger'; // primary enhance danger 不填为默认样式
    select?: boolean; // 是否和选择相关联
    disabled?: boolean;
    actionType: ACTION_TYPE;
    link?: string; // actionType === 'link'时的链接
    ajax?: AjaxOption; // // actionType === 'ajax'时的额外配置
    dialog?: DialogOption; // actionType === 'dialog'时的dialog配置
    drawer?: DrawerOption; // actionType === 'dialog'时的drawer配置
    action?: string; // actionType === 'custom'的用户自定义方法名
}

export type FilterType = 'textbox' | 'search' | 'select' | 'rangecalendar' | 'tagselect' | 'radio';

export interface FilterControl {
    type: FilterType;
    name: string;
    width?: number;
    defaultValue?: any;
    value?: any;
    label?: string;
    multi?: boolean;
    options?: any[];
    time?: boolean; // type === 'rangecalendar'时是否是时间
    range?: any; // 日期选择框的范围
    acudProps?: any;
}

// 筛选区域配置
export interface FilterConfig {
    position?: string; // 筛选位置，如果postion等于'tb'，则和操作区同行，不设置则另起一行
    submitText?: string; // 查询按钮text, 如果不设置则实时改变就发起查询。
    controls: FilterControl[];
}

const HooksList = (props: any) => {
    const {
        className,
        title,
        withToolbar,
        bulkPosition,
        filterPosition,
        bulkProps,
        filterProps,
        tableType,
        tableProps,
        tableComp,
        paginationProps,
        dialogProps,
        confirmProps,
        customRender = {}
    } = props;

    const listPageCls = classNames(className, CLS_PREFIX);
    const {Dialog, dialogOpen, onDialogCancel, onDialogSuccess} = dialogProps;
    const {Confirm} = confirmProps;

    // 主区域
    const originTableProps = useOriginalCopy(tableProps);
    const TableArea = useMemo(() => {
        if (tableType === 'custom') {
            const TableComp = tableComp;
            return (<TableComp {...originTableProps} />);
        }
        else if (tableType === 'card') {
            return (<Cards {...originTableProps} />);
        }
        else if (tableType === 'table') {
            return (<Table {...originTableProps} />);
        }
        return null;
    }, [tableType, tableComp, originTableProps]);

    return (
        <div className={listPageCls}>
            {customRender.title}
            {title && (<h2>{title}</h2>)}
            {withToolbar && (
                <div className={`${CLS_PREFIX}-toolbar`}>
                    <div style={{overflow: 'hidden'}}>
                        <div className={`${CLS_PREFIX}-toolbar-left`}>
                            <BulkArea {...bulkProps} position="left" />
                            {(filterPosition === 'left' || filterPosition === 'tb') && (
                                <FilterArea {...filterProps} position="left" />
                            )}
                        </div>
                        <div className={`${CLS_PREFIX}-toolbar-right`}>
                            {filterPosition === 'right' && (
                                <FilterArea {...filterProps} position="right" />
                            )}
                            {bulkPosition !== 'left' && (
                                <BulkArea {...bulkProps} position="right" />
                            )}
                        </div>
                    </div>
                    {!filterPosition && (
                        <FilterArea {...filterProps} />
                    )}
                </div>
            )}
            {customRender.toolbar}
            {TableArea}
            <Pagination className={`${CLS_PREFIX}-pagination`} {...paginationProps} />
            {Confirm}
            {Dialog && (
                <Dialog
                    open={dialogOpen}
                    onCancel={onDialogCancel}
                    onSuccess={onDialogSuccess}
                />
            )}
        </div>
    );
};

export default HooksList;
